/**
 * Compile a UI-side x-allOf rule into a BD-friendly plain-English narrative
 * for the read-only "Conditional Rules" section.
 *
 * Rule shape (see serialization.js):
 *   { ifConditions: [{ field, operator, value?, required }],
 *     thenFields:    [{ fieldName, required, ... }],
 *     elseFields:    [...],
 *     negated: boolean }
 *
 * Returns:
 *   {
 *     condition: 'If <field> equals <value> and <field> is at least <value>',
 *     then:      'block_zone_name is required'  OR
 *                { headline: '5 fields are required:', fields: ['a', 'b', ...] },
 *     elsePart:  'block_zone_name is optional' | null
 *   }
 *
 * The compiler stays purely data — rendering is the caller's job (so the
 * one-liner can choose to drop "If" prefixes or join with "and" inline).
 */

const OPERATOR_PHRASES = {
	const: { verb: 'equals' },
	enum: { verb: 'is one of' },
	not_in: { verb: 'is not one of' },
	minimum: { verb: 'is at least' },
	maximum: { verb: 'is at most' },
	exclusive_minimum: { verb: 'is greater than' },
	exclusive_maximum: { verb: 'is less than' },
	contains_const: { verb: 'contains' },
	type_is_null: { verb: 'is empty', valueless: true },
};

function formatValue(value) {
	if (Array.isArray(value)) return value.map(String).join(', ');
	if (value == null) return '';
	const s = String(value);
	if (s === '') return '""';
	return s;
}

function describeCondition(cond) {
	const op = OPERATOR_PHRASES[cond?.operator] || { verb: cond?.operator || 'matches' };
	if (op.valueless) {
		return `${cond.field || '(unspecified field)'} ${op.verb}`;
	}
	return `${cond.field || '(unspecified field)'} ${op.verb} ${formatValue(cond.value)}`;
}

function describeConditions(ifConditions, negated) {
	const parts = (ifConditions || [])
		.filter((c) => c && (c.field || c.operator))
		.map(describeCondition);
	if (parts.length === 0) return negated ? 'Unless (no condition set)' : 'If (no condition set)';
	const joined = parts.join(' and ');
	return negated ? `Unless ${joined}` : `If ${joined}`;
}

function describeRequiredness(fields) {
	const required = fields.filter((f) => f && f.required);
	const optional = fields.filter((f) => f && f.fieldName && !f.required);

	if (required.length === 0 && optional.length === 0) return null;

	// Most rules carry a single required field — the common case.
	if (required.length === 1 && optional.length === 0) {
		return { kind: 'single', text: `${required[0].fieldName} is required` };
	}
	if (required.length === 0 && optional.length === 1) {
		return { kind: 'single', text: `${optional[0].fieldName} is optional` };
	}

	// Multi-field — show as a bulleted-style headline + list.
	if (required.length > 1 && optional.length === 0) {
		return {
			kind: 'list',
			headline: `${required.length} fields are required:`,
			fields: required.map((f) => f.fieldName),
		};
	}

	// Mixed: prefer required count for the headline; surface optional fields
	// in a secondary clause.
	const headlineParts = [];
	if (required.length > 0) {
		headlineParts.push(`${required.length} required`);
	}
	if (optional.length > 0) {
		headlineParts.push(`${optional.length} optional`);
	}
	return {
		kind: 'list',
		headline: `${headlineParts.join(' + ')} fields:`,
		fields: [
			...required.map((f) => `${f.fieldName} (required)`),
			...optional.map((f) => `${f.fieldName} (optional)`),
		],
	};
}

export function compileRuleNarrative(rule) {
	const condition = describeConditions(rule?.ifConditions, !!rule?.negated);
	const thenPart = describeRequiredness(rule?.thenFields || []);
	const elsePart = describeRequiredness(rule?.elseFields || []);

	return {
		condition,
		then: thenPart,
		elsePart,
	};
}

/**
 * Flatten the narrative object into a single one-line sentence — useful for
 * the collapsed headline.
 */
export function compileRuleHeadline(rule) {
	const { condition, then, elsePart } = compileRuleNarrative(rule);
	const segments = [condition];
	if (then) {
		if (then.kind === 'single') {
			segments.push(`then ${then.text}`);
		} else {
			segments.push(`then ${then.headline} ${then.fields.slice(0, 3).join(', ')}${then.fields.length > 3 ? ', …' : ''}`);
		}
	} else {
		segments.push('then (no constraint set)');
	}
	if (elsePart) {
		if (elsePart.kind === 'single') {
			segments.push(`else ${elsePart.text}`);
		} else {
			segments.push(`else ${elsePart.headline} ${elsePart.fields.slice(0, 3).join(', ')}${elsePart.fields.length > 3 ? ', …' : ''}`);
		}
	}
	return segments.join(' ');
}

/**
 * Pluck the set of fieldNames referenced by a rule's IF / THEN / ELSE
 * clauses. Used by the "Conditional" badge generator on the per-field
 * view panel.
 */
export function fieldsReferencedByRule(rule) {
	const out = new Set();
	for (const c of rule?.ifConditions || []) {
		if (c?.field) out.add(c.field);
	}
	for (const f of rule?.thenFields || []) {
		if (f?.fieldName) out.add(f.fieldName);
	}
	for (const f of rule?.elseFields || []) {
		if (f?.fieldName) out.add(f.fieldName);
	}
	return out;
}

/**
 * Build a Map<fieldName, ruleIndex[]> so a row can show "Conditional —
 * referenced in Rule 1, Rule 3" without re-walking every rule per field.
 */
export function buildFieldToRulesIndex(rules) {
	const index = new Map();
	(rules || []).forEach((rule, ruleIdx) => {
		for (const name of fieldsReferencedByRule(rule)) {
			if (!index.has(name)) index.set(name, []);
			index.get(name).push(ruleIdx);
		}
	});
	return index;
}
